import { useEffect, useRef } from 'react'

import {
  thresholdCouponOfferAppliedEvent,
  thresholdCouponOfferDismissedEvent,
  thresholdCouponOfferShownEvent,
  thresholdCouponRaisedEvent,
  thresholdCouponRemovedEvent,
  thresholdCouponUnlockedEvent,
} from 'src/Helpers/TagManager'
import { useAppSelector } from 'src/Hooks/redux'
import {
  ThresholdCouponState,
  UseThresholdCoupon,
} from 'src/Hooks/useThresholdCoupon'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'

type UseThresholdCouponAnalyticsParams = {
  threshold: UseThresholdCoupon
  isHidden: boolean
  campaignId: number
  platformName: string
}

type ThresholdCouponAnalytics = {
  trackOfferApplied: () => void
  trackOfferDismissed: () => void
  trackRaised: () => void
  trackRemoved: () => void
}

export const useThresholdCouponAnalytics = ({
  threshold,
  isHidden,
  campaignId,
  platformName,
}: UseThresholdCouponAnalyticsParams): ThresholdCouponAnalytics => {
  const userId = useAppSelector(getUserId)
  const isOfferShownRef = useRef(false)
  const previousStateRef = useRef(threshold.state)

  const payload = {
    campaignId,
    platformName,
    userId: userId || void 0,
    couponCode: threshold.couponCode,
    minSpend: threshold.threshold,
    orderTotal: threshold.eligibleTotal,
    remaining: threshold.remaining,
  }
  const payloadRef = useRef(payload)

  const { state } = threshold

  useEffect(() => {
    payloadRef.current = payload
  })

  useEffect(() => {
    if (isHidden || state !== ThresholdCouponState.Offered) {
      return
    }
    if (!isOfferShownRef.current) {
      isOfferShownRef.current = true
      thresholdCouponOfferShownEvent(payloadRef.current)
    }
  }, [state, isHidden])

  useEffect(() => {
    const previous = previousStateRef.current
    previousStateRef.current = state
    if (
      !isHidden &&
      previous === ThresholdCouponState.AttachedBelowThreshold &&
      state === ThresholdCouponState.Unlocked
    ) {
      thresholdCouponUnlockedEvent(payloadRef.current)
    }
  }, [state, isHidden])

  return {
    trackOfferApplied: () => thresholdCouponOfferAppliedEvent(payload),
    trackOfferDismissed: () => thresholdCouponOfferDismissedEvent(payload),
    trackRaised: () =>
      thresholdCouponRaisedEvent({
        ...payload,
        orderTotalBefore: threshold.eligibleTotal,
        orderTotalAfter: threshold.eligibleTotal + threshold.remaining,
      }),
    trackRemoved: () => thresholdCouponRemovedEvent(payload),
  }
}
